import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import React, { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CancelButton, ForwardButton } from "../Primitives/Buttons";
import { IPomReportingTableData } from "../reporting-table/pom-reporting-table-data";
import { formatQuantity } from "../reporting-table/reporting-quantity.helper";
import {
  formatLineTotal,
  formatMoney,
  formatUnitPriceOrPlaceholder,
  hasAnyPrice,
  hasVisibleChange,
  periodTotals,
} from "../reporting-table/reporting-price.helper";
import { hasPendingChange } from "../../pages/announcement/pom-reporting-unsaved-state.util";

/** Columns shown regardless of pricing: position, description, unit, quantity. */
const BASE_COLUMN_COUNT = 4;

const useStyles = makeStyles()({
  table: {
    marginTop: "8px",
  },
  headerCell: {
    fontWeight: "bold",
  },
  totalCell: {
    fontWeight: "bold",
    whiteSpace: "nowrap",
  },
  footerNote: {
    borderBottom: "none",
  },
});

interface IPomReportingSubmitDialogProps {
  open: boolean;
  /** All positions of the period: only the edited ones are listed, but the totals cover the whole period. */
  positions: IPomReportingTableData[];
  reportingPeriodLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const PomReportingSubmitDialog: FC<IPomReportingSubmitDialogProps> = (props) => {
  const { open, positions, reportingPeriodLabel, onConfirm, onCancel } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const changedPositions = positions.filter(hasPendingChange);
  const showPrices = hasAnyPrice(positions);
  const totals = periodTotals(positions);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={showPrices ? "md" : "sm"} fullWidth>
      <DialogTitle>{t("pom.reportings.submit_dialog.title")}</DialogTitle>
      <DialogContent>
        <Typography>
          <Trans
            i18nKey="pom.reportings.submit_dialog.confirm_text"
            values={{ period: reportingPeriodLabel }}
            components={{ strong: <strong /> }}
          />
        </Typography>
        <Table size="small" className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>{t("pom.reportings.submit_dialog.position_no")}</TableCell>
              <TableCell className={classes.headerCell}>{t("pom.reportings.submit_dialog.description")}</TableCell>
              <TableCell className={classes.headerCell}>{t("pom.reportings.submit_dialog.unit_of_measure")}</TableCell>
              <TableCell className={classes.headerCell} align="right">
                {t("pom.reportings.submit_dialog.quantity")}
              </TableCell>
              {showPrices && (
                <>
                  <TableCell className={classes.headerCell} align="right">
                    {t("pom.reportings.submit_dialog.unit_price")}
                  </TableCell>
                  <TableCell className={classes.headerCell} align="right">
                    {t("pom.reportings.submit_dialog.line_total")}
                  </TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {changedPositions.map((position) => (
              <TableRow key={position.positionNo}>
                <TableCell>{position.positionNo}</TableCell>
                <TableCell>{position.description}</TableCell>
                <TableCell>{position.unit ?? ""}</TableCell>
                <TableCell align="right">{formatQuantity(position.editedQuantity ?? position.currentQuantity, position.unit)}</TableCell>
                {showPrices && (
                  <>
                    <TableCell align="right">{formatUnitPriceOrPlaceholder(position.unitPrice)}</TableCell>
                    <TableCell align="right">{formatLineTotal(position)}</TableCell>
                  </>
                )}
              </TableRow>
            ))}
          </TableBody>
          {showPrices && (
            <TableFooter>
              <TableRow>
                <TableCell className={classes.totalCell} colSpan={BASE_COLUMN_COUNT + 1} align="right">
                  {t("pom.reportings.submit_dialog.total_net")}
                </TableCell>
                <TableCell className={classes.totalCell} align="right">
                  {formatMoney(totals.total)}
                </TableCell>
              </TableRow>
              {hasVisibleChange(totals.changeTotal) && (
                <TableRow>
                  <TableCell className={classes.totalCell} colSpan={BASE_COLUMN_COUNT + 1} align="right">
                    {t("pom.reportings.submit_dialog.total_change")}
                  </TableCell>
                  <TableCell className={classes.totalCell} align="right">
                    {formatMoney(totals.changeTotal)}
                  </TableCell>
                </TableRow>
              )}
              {totals.unpricedCount > 0 && (
                <TableRow>
                  <TableCell className={classes.footerNote} colSpan={BASE_COLUMN_COUNT + 2}>
                    {t("pom.reportings.submit_dialog.unpriced_note", { count: totals.unpricedCount })}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell className={classes.footerNote} colSpan={BASE_COLUMN_COUNT + 2}>
                  {t("pom.reportings.submit_dialog.vat_note")}
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "space-between" }}>
        <CancelButton onClick={onCancel}>
          <Typography variant="body1">{t("pom.reportings.submit_dialog.cancel")}</Typography>
        </CancelButton>
        <ForwardButton onClick={onConfirm}>
          <Typography variant="body1">{t("pom.reportings.submit_dialog.ok")}</Typography>
        </ForwardButton>
      </DialogActions>
    </Dialog>
  );
};
